import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';
// Defines <dialog-component>, the overlay menu (snippets/birrot-header-overlay.liquid).
import { DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';

/**
 * The event sections/birrot-slideshow.liquid dispatches when a slide has
 * arrived, with that slide's header colours in `detail`.
 */
const SLIDE_CHANGE = 'birrot:slide-change';

/**
 * Laid into the signed-in <shopify-account>'s shadow root: no avatar, and
 * the button stretched over the header's own ACCOUNT. The component
 * redraws its button after each `shopify-render`, which it fires on
 * `document`; the sheet is re-adopted then, in case the component has
 * replaced its sheets.
 */
const ACCOUNT_SHEET = new CSSStyleSheet();
ACCOUNT_SHEET.replaceSync(`
  #shopify-element-wrapper, .wrapper, .account-button { width: 100%; height: 100%; }
  .account-button__avatar { display: none; }
`);

/**
 * The BIRROT header (sections/birrot-header.liquid), Showcase's
 * #site-control.
 *
 * - The burger opens the drawer below 1100px. The drawer is a non-modal
 *   dialog so the header stays above it, which leaves the page focusable:
 *   while it is open focus is trapped in the header (the burger, now the
 *   close button, the header's actions and the drawer), Escape and a click
 *   beside the drawer close it, and the root scroll is locked as
 *   DialogComponent locks it.
 * - MENU opens the overlay, a <dialog-component>, by its `on:click`. A
 *   nested menu's button there toggles its links (`aria-expanded`, which
 *   the overlay's CSS reads), one open at a time; keyboard focus on it opens it too, and focus or a
 *   mouse leaving the nest closes it, as hover would; closing the overlay
 *   closes them all. Hover shows it by CSS alone. Keyboard focus is
 *   script, not :focus-visible in CSS: Chromium blurs the button before
 *   the next link takes focus, and a list hidden in that gap refuses it.
 * - Opening the drawer or the overlay focuses the dialog itself, not its
 *   first link: a dialog's first control gets the focus ring in Safari
 *   (and in Chromium for an open by script), which reads as a black box
 *   round MENU or New Arrivals. Tab still reaches the first link.
 * - CART opens the cart drawer (openCart), and links to /cart where there
 *   is none (the cart page).
 * - The cart count ("CART (n)", and the `data-cart-count` the mobile dot
 *   reads) follows every cart change, and is re-read when the page comes
 *   back from the back-forward cache, where it would otherwise show the
 *   count it was left with.
 * - On the home page the slideshow reports each slide's colours; the
 *   header's stylesheet maps them onto MENU and onto SEARCH and CART.
 * - Signed in, the account component's avatar gives way to the header's
 *   ACCOUNT (ACCOUNT_SHEET).
 * - The announcement bar's height is kept in --birrot-announcement-height
 *   on :root, which the page offset and everything under the header add
 *   to the header's own; it wraps on narrow screens, and is 0 when hidden.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} burger - Opens and closes the drawer.
 * @property {HTMLDialogElement} drawer - The menu drawer.
 * @property {HTMLAnchorElement} cartLink - The cart action.
 * @property {HTMLElement} cartCount - The number in "CART (n)".
 * @property {HTMLElement} [overlay] - The overlay's <dialog-component> (from 1100px).
 * @property {HTMLElement} [account] - The <shopify-account>, signed in only.
 * @property {HTMLElement} [announcement] - The announcement bar, when on.
 *
 * @extends {Component<Refs>}
 */
class BirrotHeader extends Component {
  requiredRefs = ['burger', 'drawer', 'cartLink', 'cartCount'];

  /** @type {ResizeObserver | undefined} */
  #announcementObserver;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    document.addEventListener(SLIDE_CHANGE, this.#onSlideChange);
    window.addEventListener('pageshow', this.#onPageShow);
    this.refs.overlay?.addEventListener(DialogOpenEvent.eventName, this.#focusOverlay);
    this.refs.overlay?.addEventListener(DialogCloseEvent.eventName, this.#closeNests);
    this.refs.overlay?.addEventListener('click', this.#onOverlayClick);
    this.refs.overlay?.addEventListener('pointerout', this.#onOverlayPointerOut);
    this.refs.overlay?.addEventListener('focusin', this.#onOverlayFocusIn);
    this.refs.overlay?.addEventListener('focusout', this.#onOverlayFocusOut);
    if (this.refs.account) {
      document.addEventListener('shopify-render', this.#coverAccount);
      this.#coverAccount();
    }
    if (this.refs.announcement) {
      this.#announcementObserver = new ResizeObserver(this.#measureAnnouncement);
      this.#announcementObserver.observe(this.refs.announcement);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    document.removeEventListener(SLIDE_CHANGE, this.#onSlideChange);
    window.removeEventListener('pageshow', this.#onPageShow);
    this.refs.overlay?.removeEventListener(DialogOpenEvent.eventName, this.#focusOverlay);
    this.refs.overlay?.removeEventListener(DialogCloseEvent.eventName, this.#closeNests);
    this.refs.overlay?.removeEventListener('click', this.#onOverlayClick);
    this.refs.overlay?.removeEventListener('pointerout', this.#onOverlayPointerOut);
    this.refs.overlay?.removeEventListener('focusin', this.#onOverlayFocusIn);
    this.refs.overlay?.removeEventListener('focusout', this.#onOverlayFocusOut);
    document.removeEventListener('shopify-render', this.#coverAccount);
    this.#announcementObserver?.disconnect();
    if (this.refs.drawer?.open) this.#releaseDrawer();
  }

  #measureAnnouncement = () => {
    const height = this.refs.announcement?.getBoundingClientRect().height ?? 0;
    document.documentElement.style.setProperty('--birrot-announcement-height', `${height}px`);
  };

  toggleDrawer = () => {
    if (this.refs.drawer.open) this.closeDrawer();
    else this.openDrawer();
  };

  openDrawer() {
    const { drawer, burger } = this.refs;
    if (drawer.open) return;

    drawer.show();
    drawer.focus();
    this.classList.add('birrot-header--drawer-open');
    burger.setAttribute('aria-expanded', 'true');
    lockScroll(drawer);
    trapFocus(this);
    document.addEventListener('keydown', this.#onKeyDown);
    document.addEventListener('click', this.#onDocumentClick);
  }

  /**
   * @param {object} [options]
   * @param {boolean} [options.restoreFocus] - Focus the burger after (the default); false
   *   leaves the focus where it is, for a drawer closed on the way to another.
   */
  closeDrawer({ restoreFocus = true } = {}) {
    const { drawer, burger } = this.refs;
    if (!drawer.open) return;

    drawer.close();
    this.classList.remove('birrot-header--drawer-open');
    burger.setAttribute('aria-expanded', 'false');
    this.#releaseDrawer();
    if (restoreFocus) burger.focus();
  }

  #focusOverlay = () => {
    this.refs.overlay?.querySelector('dialog')?.focus();
  };

  /**
   * A nested menu's button toggles it, and closes any other.
   * @param {MouseEvent} event
   */
  #onOverlayClick = (event) => {
    const button = /** @type {Element} */ (event.target).closest?.('[data-birrot-nest-toggle]');
    if (!button) return;

    if (button.getAttribute('aria-expanded') === 'true') this.#setNest(button, false);
    else this.#openNest(button);
  };

  /**
   * A mouse leaving a nest closes it, so a click does not keep it open
   * after the pointer has gone, where hover alone would close it.
   * @param {PointerEvent} event
   */
  #onOverlayPointerOut = (event) => {
    if (event.pointerType === 'mouse') this.#leaveNest(event);
  };

  /**
   * Keyboard focus on a nest's button opens it (not a mouse click's focus,
   * which Chromium gives a button: hover has that).
   * @param {FocusEvent} event
   */
  #onOverlayFocusIn = (event) => {
    const button = /** @type {Element} */ (event.target);
    if (!button.matches?.('[data-birrot-nest-toggle]:focus-visible')) return;
    if (button.getAttribute('aria-expanded') !== 'true') this.#openNest(button);
  };

  /**
   * Focus leaving a nest closes it, so Tab past one leaves it closed, and
   * so does focus going to nothing (a click on blank space, the window
   * losing focus), unless the pointer is on the nest, where hover shows
   * it and the pointer's leaving closes it.
   * @param {FocusEvent} event
   */
  #onOverlayFocusOut = (event) => {
    if (event.relatedTarget) {
      this.#leaveNest(event);
      return;
    }
    const nest = /** @type {Element} */ (event.target).closest?.('.birrot-menu-overlay__nest');
    if (nest && !nest.matches(':hover')) this.#leaveNest(event);
  };

  /**
   * @param {PointerEvent | FocusEvent} event
   */
  #leaveNest(event) {
    const nest = /** @type {Element} */ (event.target).closest?.('.birrot-menu-overlay__nest');
    if (!nest || nest.contains(/** @type {Node | null} */ (event.relatedTarget))) return;

    const button = nest.querySelector('[data-birrot-nest-toggle][aria-expanded="true"]');
    if (button) this.#setNest(button, false);
  }

  #closeNests = () => {
    for (const button of this.refs.overlay?.querySelectorAll('[data-birrot-nest-toggle][aria-expanded="true"]') ?? []) {
      this.#setNest(button, false);
    }
  };

  /**
   * Opens one nest, closing any other.
   * @param {Element} button
   */
  #openNest(button) {
    this.#closeNests();
    this.#setNest(button, true);
  }

  /**
   * @param {Element} button
   * @param {boolean} open
   */
  #setNest(button, open) {
    button.setAttribute('aria-expanded', String(open));
  }

  #coverAccount = () => {
    const root = this.refs.account?.shadowRoot;
    if (root && !root.adoptedStyleSheets.includes(ACCOUNT_SHEET)) {
      root.adoptedStyleSheets = [...root.adoptedStyleSheets, ACCOUNT_SHEET];
    }
  };

  /** Undoes what openDrawer set up outside the header. */
  #releaseDrawer() {
    unlockScroll(this.refs.drawer);
    removeTrapFocus();
    document.removeEventListener('keydown', this.#onKeyDown);
    document.removeEventListener('click', this.#onDocumentClick);
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key === 'Escape') this.closeDrawer();
  };

  /**
   * A click on the page beside the drawer closes it.
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    const { drawer, burger } = this.refs;
    const target = /** @type {Node} */ (event.target);
    if (!drawer.contains(target) && !burger.contains(target)) this.closeDrawer();
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #onCartLinesUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const count = cart?.totalQuantity ?? detail?.itemCount;
        if (count !== undefined) this.#renderCount(count);
      })
      .catch(() => {});
  };

  /**
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (!event.persisted) return;

    fetch(`${Theme.routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => this.#renderCount(cart.item_count))
      .catch(() => {});
  };

  /**
   * CART opens the cart drawer (snippets/birrot-cart-drawer.liquid) where
   * there is one; on the cart page, or with a modifier key, it is the link
   * to /cart it is written as.
   * @param {MouseEvent} event
   */
  openCart(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
    if (typeof drawer?.open !== 'function') return;

    event.preventDefault();
    // theme-drawer takes document.activeElement as the element to return
    // to on close, and that should be CART: not the burger's focus, and not
    // body, where Safari leaves it after a click on a link (it does not
    // focus links on click).
    if (this.refs.drawer.open) this.closeDrawer({ restoreFocus: false });
    this.refs.cartLink.focus({ preventScroll: true });
    drawer.open();
  }

  /**
   * @param {number} count
   */
  #renderCount(count) {
    this.refs.cartLink.dataset.cartCount = String(count);
    this.refs.cartCount.textContent = String(count);
  }

  /**
   * @param {CustomEvent<{ menuColor?: string, navColor?: string }>} event
   */
  #onSlideChange = (event) => {
    const { menuColor, navColor } = event.detail ?? {};
    if (menuColor) this.style.setProperty('--birrot-slide-ink', menuColor);
    if (navColor) this.style.setProperty('--birrot-slide-ink-actions', navColor);
  };
}

if (!customElements.get('birrot-header')) {
  customElements.define('birrot-header', BirrotHeader);
}
